import type { Button as AriaButton } from '@ariakit/react'
import { clsx } from 'clsx'
import type React from 'react'
import { Button } from './button'

export type CardVariant = 'subtle' | 'white' | 'outline' | 'plain' | 'default'

// Card Root Component
export interface CardRootProps {
  variant?: CardVariant
  /** Optional padding override. Defaults to `'md'` (p-4); `'lg'` is p-6 + gap-6. */
  padding?: 'md' | 'lg'
  /** Optional corner-radius override. Variant-specific default; pass `'2xl'` for 16px corners. */
  rounded?: '2xl'
  className?: string
  children: React.ReactNode
}

export function CardRoot({
  variant = 'default',
  padding,
  rounded,
  className,
  children,
}: CardRootProps) {
  const variantClasses = {
    subtle: 'bg-sand-25 rounded-md',
    white: 'bg-white shadow border border-edge-stroke rounded-lg',
    outline: 'bg-white border border-edge-stroke rounded shadow-none',
    plain: 'bg-white rounded-2xl',
    default: 'bg-sand-25 shadow rounded-lg',
  }

  return (
    <div
      className={clsx(
        'flex flex-col overflow-hidden relative',
        padding === 'lg' ? 'p-6 gap-6' : 'p-4',
        variantClasses[variant],
        rounded === '2xl' && 'rounded-2xl',
        className
      )}
    >
      {children}
    </div>
  )
}

// Card Header Component
export interface CardHeaderProps {
  className?: string
  children: React.ReactNode
}

export function CardHeader({ className, children }: CardHeaderProps) {
  return <div className={clsx('flex items-start justify-between', className)}>{children}</div>
}

// Card Title Component
export interface CardTitleProps {
  className?: string
  children: React.ReactNode
}

export function CardTitle({ className, children }: CardTitleProps) {
  return <h3 className={clsx('font-ginto-nord-md text-charcoal text-xl', className)}>{children}</h3>
}

// Card Content Component
export interface CardContentProps {
  className?: string
  children: React.ReactNode
}

export function CardContent({ className, children }: CardContentProps) {
  return <div className={clsx(className)}>{children}</div>
}

// Card Action Component
export interface CardActionProps extends React.ComponentPropsWithoutRef<typeof AriaButton> {
  variant?: 'primary' | 'secondary' | 'tertiary' | 'text' | 'danger' | 'link' | 'none'
  children: React.ReactNode
}

export function CardAction({ className, variant = 'secondary', ...props }: CardActionProps) {
  return (
    <Button variant={variant} className={clsx('absolute top-4 right-4', className)} {...props} />
  )
}

// Compound Component Exports
export const Root = CardRoot
export const Header = CardHeader
export const Title = CardTitle
export const Content = CardContent
export const Action = CardAction
